import { MockServiceStore } from "./MockServiceStore";

/**
 * PO Status workflow
 */
export type POStatus =
  | "draft"
  | "submitted"
  | "pending_receiving"
  | "partially_received"
  | "partial_received"
  | "received"
  | "pending_approval"
  | "approved"
  | "rejected"
  | "payment_scheduled"
  | "partial_paid"
  | "paid"
  | "cancelled";

export type POPriority = "low" | "medium" | "high" | "urgent";
export type FreightType = "none" | "allocated" | "third_party";

/**
 * Purchase Order entity type
 */
export interface PurchaseOrder {
  purchaseOrderId: number;
  poNumber: string;
  vendorId: number;
  vendorName?: string; // Denormalized for display
  jobId?: number;
  jobNumber?: string; // Denormalized for display
  plantId: number;
  plantCode?: string; // Denormalized for display
  requestedBy: number;
  requestedByName?: string; // Denormalized for display
  approvedBy?: number;
  approvedByName?: string; // Denormalized for display
  quoteAmount: number;
  totalAmount: number;
  taxAmount: number;
  freightAmount: number;
  currency: string;
  freightType: FreightType;
  freightCarrierId?: number;
  freightCarrierName?: string;
  freightInvoiceNumber?: string;
  orderDate: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  receivedDate?: string;
  lastReceivedDate?: string;
  approvedDate?: string;
  paidDate?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceDueDate?: string;
  paidAmount?: number;
  status: POStatus;
  priority: POPriority;
  shippingAddress?: string;
  billingAddress?: string;
  deliveryLocation?: string;
  hasQuoteDocument: boolean;
  hasPackingSlip: boolean;
  hasInvoice: boolean;
  hasFreightBill: boolean;
  notes?: string;
  specialInstructions?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  updatedBy: number;
}

/**
 * Generate a PO number
 */
const generatePONumber = (date: Date, index: number): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `PO-${year}${month}${day}-${String(index).padStart(3, "0")}`;
};

/**
 * Mock vendor names for display
 */
const vendorNames = [
  "LW Products",
  "Sutton Trucking",
  "Pacific Steel Supply",
  "Cascade Aggregates",
  "Northwest Rebar",
  "Columbia Concrete",
  "Valley Materials",
  "Mountain Steel",
  "Precision Forms",
  "Quality Hardware",
];

/**
 * Mock job numbers for display
 */
const jobNumbers = [
  "25-1001",
  "25-1002",
  "25-1003",
  "25-1004",
  "25-1005",
  "24-9987",
  "24-9988",
  "24-9989",
  "24-9990",
  null,
];

/**
 * Generate mock purchase orders
 */
const generateMockPurchaseOrders = (): PurchaseOrder[] => {
  const orders: PurchaseOrder[] = [];
  const statuses: POStatus[] = [
    "draft",
    "submitted",
    "pending_receiving",
    "partially_received",
    "received",
    "pending_approval",
    "approved",
    "rejected",
    "payment_scheduled",
    "paid",
  ];
  const priorities: POPriority[] = ["low", "medium", "high", "urgent"];
  const freightTypes: FreightType[] = ["none", "allocated", "third_party"];
  const plantCodes = ["100", "200", "300", "400", "500", "600", "700"];

  for (let i = 1; i <= 50; i++) {
    const orderDate = new Date();
    orderDate.setDate(orderDate.getDate() - Math.floor(Math.random() * 60));

    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const priority = priorities[Math.floor(Math.random() * priorities.length)];
    const freightType = freightTypes[Math.floor(Math.random() * freightTypes.length)];
    const plantIndex = Math.floor(Math.random() * 7);
    const vendorIndex = Math.floor(Math.random() * vendorNames.length);
    const jobIndex = Math.floor(Math.random() * jobNumbers.length);

    const quoteAmount = Math.round(Math.random() * 50000 + 500);
    const taxAmount = Math.round(quoteAmount * 0.08);
    const freightAmount = freightType !== "none" ? Math.round(Math.random() * 2000 + 100) : 0;
    const totalAmount = quoteAmount + taxAmount + freightAmount;

    const expectedDeliveryDate = new Date(orderDate);
    expectedDeliveryDate.setDate(expectedDeliveryDate.getDate() + Math.floor(Math.random() * 30) + 5);

    orders.push({
      purchaseOrderId: i,
      poNumber: generatePONumber(orderDate, i),
      vendorId: vendorIndex + 1,
      vendorName: vendorNames[vendorIndex],
      jobId: jobNumbers[jobIndex] ? jobIndex + 1 : undefined,
      jobNumber: jobNumbers[jobIndex] || undefined,
      plantId: plantIndex + 1,
      plantCode: plantCodes[plantIndex],
      requestedBy: Math.floor(Math.random() * 10) + 1,
      requestedByName: `Salesperson ${Math.floor(Math.random() * 10) + 1}`,
      approvedBy: status === "approved" || status === "paid" ? 1 : undefined,
      approvedByName: status === "approved" || status === "paid" ? "Clay (CFO)" : undefined,
      quoteAmount,
      totalAmount,
      taxAmount,
      freightAmount,
      currency: "USD",
      freightType,
      freightCarrierId: freightType === "third_party" ? 1 : undefined,
      freightCarrierName: freightType === "third_party" ? "Sutton Trucking" : undefined,
      freightInvoiceNumber: freightType === "third_party" ? `FRT-${i}${Math.floor(Math.random() * 1000)}` : undefined,
      orderDate: orderDate.toISOString().split("T")[0],
      expectedDeliveryDate: expectedDeliveryDate.toISOString().split("T")[0],
      actualDeliveryDate:
        ["received", "pending_approval", "approved", "paid"].includes(status)
          ? expectedDeliveryDate.toISOString().split("T")[0]
          : undefined,
      receivedDate:
        ["received", "pending_approval", "approved", "paid"].includes(status)
          ? expectedDeliveryDate.toISOString().split("T")[0]
          : undefined,
      approvedDate:
        ["approved", "payment_scheduled", "paid"].includes(status)
          ? new Date().toISOString().split("T")[0]
          : undefined,
      paidDate: status === "paid" ? new Date().toISOString().split("T")[0] : undefined,
      status,
      priority,
      shippingAddress: `Plant ${plantCodes[plantIndex]}, Portland, OR`,
      billingAddress: "Miller Precast Corporate, 123 Main St, Portland, OR 97201",
      deliveryLocation: `Plant ${plantCodes[plantIndex]}`,
      hasQuoteDocument: Math.random() > 0.3,
      hasPackingSlip: ["received", "pending_approval", "approved", "paid"].includes(status),
      hasInvoice: ["pending_approval", "approved", "payment_scheduled", "paid"].includes(status),
      hasFreightBill: freightType === "third_party" && ["received", "pending_approval", "approved", "paid"].includes(status),
      notes: Math.random() > 0.7 ? "Standard delivery" : undefined,
      specialInstructions: priority === "urgent" ? "Rush order - expedite shipping" : undefined,
      rejectionReason: status === "rejected" ? "Quote discrepancy - amounts don't match" : undefined,
      createdAt: orderDate.toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: Math.floor(Math.random() * 10) + 1,
      updatedBy: Math.floor(Math.random() * 10) + 1,
    });
  }

  return orders;
};

/**
 * Mock purchase orders data
 */
export const mockPurchaseOrders: PurchaseOrder[] = generateMockPurchaseOrders();

/**
 * Create mock purchase orders store
 */
export const createMockPurchaseOrdersStore = () =>
  new MockServiceStore<PurchaseOrder>({
    mockData: mockPurchaseOrders,
    idField: "purchaseOrderId",
    simulateDelay: 300,
  });

export default mockPurchaseOrders;
